import React, { useMemo, useState } from 'react';
import { Minus, Plus, QrCode, Trash2 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ProductTypeBadge from '@/components/haccp/ProductTypeBadge';
import { convertUnitQuantity, normalizeUnitValue } from '@/lib/units';

const INTEGER_UNITS = new Set(['pz', 'conf', 'vaschetta', 'busta', 'cartone', 'g', 'mg', 'ml', 'cl']);
const quantityStep = (unit) => (INTEGER_UNITS.has(normalizeUnitValue(unit)) ? 1 : 0.01);
const tidyQty = (value) => Math.round((Number(value) + Number.EPSILON) * 10000) / 10000;
const dateLabel = (value) => (value ? new Date(value).toLocaleDateString('it-IT') : '—');

function ManualLotCombobox({ open, onOpenChange, options, selectedLot, ingredientName, onSelect, disabled }) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-11 w-full min-w-0 justify-between px-3 text-left font-normal sm:h-12"
        >
          <span className={`min-w-0 truncate ${selectedLot ? 'text-foreground' : 'text-muted-foreground'}`}>
            {selectedLot
              ? `${selectedLot.lot_number || 'Lotto'} · ${selectedLot.current_quantity} ${selectedLot.unit}`
              : options.length
                ? 'Cerca o seleziona lotto'
                : 'Nessun lotto disponibile'}
          </span>
          <span className="ml-2 shrink-0 text-xs text-muted-foreground">⌄</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,430px)] p-0 sm:w-[420px]">
        <Command>
          <CommandInput placeholder="Cerca numero lotto o prodotto..." />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>Nessun lotto trovato.</CommandEmpty>
            {options.map((lot, lotIndex) => (
              <CommandItem
                key={lot.lot_id}
                value={`${lot.lot_number || ''} ${lot.commercial_name || ingredientName} ${lot.current_quantity || ''} ${lot.unit || ''} ${dateLabel(lot.expiry_date)}`}
                onSelect={() => onSelect(lot.lot_id)}
                className="items-start py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    {lotIndex === 0 && (
                      <span className="shrink-0 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-700">
                        FEFO
                      </span>
                    )}
                    <span className="truncate font-semibold">{lot.lot_number || 'Lotto'}</span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {lot.commercial_name || ingredientName} · {lot.current_quantity} {lot.unit} · scad. {dateLabel(lot.expiry_date)}
                  </p>
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ManualIngredientList({
  rows,
  ingredients,
  lots,
  lotsFor,
  ingName,
  updateInput,
  addInput,
  removeInput,
  onScan
}) {
  const [openLotIndex, setOpenLotIndex] = useState(null);
  const ingredientMap = useMemo(
    () => new Map(ingredients.map((ingredient) => [ingredient.ingredient_id, ingredient])),
    [ingredients]
  );

  const selectableIngredients = useMemo(
    () => ingredients.filter((ingredient) => ingredient.status !== 'inactive' && (ingredient.type || 'raw') !== 'final_product'),
    [ingredients]
  );

  const adjustQuantity = (index, direction) => {
    const row = rows[index];
    const step = quantityStep(row.unit);
    const current = Number(row.quantity_used || 0);
    const next = tidyQty(Math.max(step, current + direction * step));
    updateInput(index, 'quantity_used', String(next));
  };

  const canIncrease = (row, selectedLot) => {
    if (!selectedLot) return true;
    const next = tidyQty(Number(row.quantity_used || 0) + quantityStep(row.unit));
    const converted = convertUnitQuantity(next, row.unit, selectedLot.unit);
    return converted !== null && converted <= Number(selectedLot.current_quantity || 0);
  };

  if (!rows.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-dashed bg-white p-5 text-center">
        <div>
          <p className="font-semibold">Nessun ingrediente</p>
          <p className="mt-1 text-sm text-muted-foreground">Aggiungi gli ingredienti realmente utilizzati.</p>
          <Button type="button" className="mt-3" onClick={addInput}>
            <Plus /> Aggiungi ingrediente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] gap-2 sm:gap-3">
      <div className="flex items-center justify-between gap-3 px-0.5 sm:px-1">
        <div>
          <h3 className="font-heading text-xl font-semibold text-[#172033] sm:text-2xl">Ingredienti</h3>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">Inserisci ingrediente, lotto e quantità realmente utilizzata.</p>
        </div>
        <Button type="button" variant="outline" className="h-10 shrink-0" onClick={addInput}>
          <Plus /> <span className="hidden sm:inline">Aggiungi</span>
        </Button>
      </div>

      <section className="min-h-0 overflow-y-auto rounded-2xl border bg-white">
        <div className="sticky top-0 z-10 hidden grid-cols-[minmax(210px,1.15fr)_minmax(250px,1.45fr)_220px_96px] items-center gap-3 border-b bg-slate-50/95 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-[#667085] backdrop-blur lg:grid">
          <span>Ingrediente</span>
          <span>Lotto</span>
          <span>Quantità utilizzata</span>
          <span className="text-center">Azioni</span>
        </div>

        <div className="divide-y">
          {rows.map((row, index) => {
            const ingredient = ingredientMap.get(row.ingredient_id);
            const options = row.ingredient_id ? lotsFor(row.ingredient_id) : [];
            const selectedLot = lots.find((lot) => lot.lot_id === row.lot_id);
            const ingredientLabel = row.ingredient_id ? ingName(row.ingredient_id) : 'Ingrediente';

            return (
              <div
                key={`${row.ingredient_id || 'manual'}-${index}`}
                className="grid gap-2.5 p-3 sm:gap-3 sm:p-4 md:grid-cols-[minmax(170px,.9fr)_minmax(190px,1.1fr)_minmax(160px,.9fr)_96px] md:items-center md:gap-3 md:py-3 lg:grid-cols-[minmax(210px,1.15fr)_minmax(250px,1.45fr)_220px_96px] lg:px-4"
              >
                <div className="min-w-0">
                  <Label className="lg:hidden">Ingrediente *</Label>
                  <div className="mt-1.5 flex min-w-0 items-center gap-2 lg:mt-0">
                    <Select value={row.ingredient_id || undefined} onValueChange={(value) => updateInput(index, 'ingredient_id', value)}>
                      <SelectTrigger className="h-11 min-w-0 flex-1 sm:h-12">
                        <SelectValue placeholder="Seleziona ingrediente" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectableIngredients.map((item) => (
                          <SelectItem key={item.ingredient_id} value={item.ingredient_id}>
                            {item.name}{item.type === 'semifinished' ? ' · semilavorato' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50">
                      {ingredient ? <ProductTypeBadge type={ingredient.type || 'raw'} iconOnly /> : <span className="h-4 w-4" aria-hidden="true" />}
                    </span>
                  </div>
                </div>

                <div className="min-w-0">
                  <Label className="lg:hidden">Lotto *</Label>
                  <div className="mt-1.5 lg:mt-0">
                    <ManualLotCombobox
                      open={openLotIndex === index}
                      onOpenChange={(open) => setOpenLotIndex(open ? index : null)}
                      options={options}
                      selectedLot={selectedLot}
                      ingredientName={ingredientLabel}
                      onSelect={(lotId) => {
                        setOpenLotIndex(null);
                        updateInput(index, 'lot_id', lotId);
                      }}
                      disabled={!row.ingredient_id || (options.length === 0 && !selectedLot)}
                    />
                  </div>
                  {selectedLot && (
                    <p className="mt-1 truncate text-[11px] text-muted-foreground">
                      Disponibili {selectedLot.current_quantity} {selectedLot.unit} · scad. {dateLabel(selectedLot.expiry_date)}
                    </p>
                  )}
                  {row.ingredient_id && options.length === 0 && !selectedLot && (
                    <p className="mt-1 text-[11px] font-medium text-red-600">Nessun lotto attivo e non scaduto disponibile.</p>
                  )}
                </div>

                <div className="min-w-0">
                  <Label className="lg:hidden">Quantità utilizzata *</Label>
                  <div className="mt-1.5 grid grid-cols-[44px_minmax(0,1fr)_44px_auto] items-center overflow-hidden rounded-md border bg-white sm:grid-cols-[46px_minmax(0,1fr)_46px_auto] lg:mt-0">
                    <button
                      type="button"
                      className="flex h-11 items-center justify-center border-r text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35 sm:h-12"
                      onClick={() => adjustQuantity(index, -1)}
                      disabled={Number(row.quantity_used || 0) <= quantityStep(row.unit)}
                      aria-label={`Riduci quantità di ${ingredientLabel}`}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <Input
                      className="h-11 min-w-0 rounded-none border-0 text-center shadow-none focus-visible:ring-0 sm:h-12"
                      type="number"
                      min="0"
                      step={quantityStep(row.unit)}
                      value={row.quantity_used}
                      onChange={(event) => updateInput(index, 'quantity_used', event.target.value)}
                      placeholder="0"
                      aria-label={`Quantità utilizzata di ${ingredientLabel}`}
                    />
                    <button
                      type="button"
                      className="flex h-11 items-center justify-center border-l text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35 sm:h-12"
                      onClick={() => adjustQuantity(index, 1)}
                      disabled={!canIncrease(row, selectedLot)}
                      aria-label={`Aumenta quantità di ${ingredientLabel}`}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <span className="flex h-11 items-center border-l bg-slate-50 px-2 text-xs font-semibold text-slate-600 sm:h-12">
                      {row.unit}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1 md:justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-11 w-11 border-blue-200 text-primary hover:bg-blue-50 sm:h-12 sm:w-12"
                    onClick={() => onScan(index)}
                    disabled={!row.ingredient_id}
                    aria-label={`Scansiona lotto per ${ingredientLabel}`}
                    title="Scansiona QR lotto"
                  >
                    <QrCode />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 text-red-600 hover:bg-red-50 hover:text-red-700 sm:h-12 sm:w-12"
                    onClick={() => removeInput(index)}
                    disabled={rows.length === 1}
                    aria-label={`Rimuovi ${ingredientLabel}`}
                    title="Rimuovi ingrediente"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
